import { useState, useRef, useEffect } from "react";
import { useAdminEdit } from "@/contexts/AdminEditContext";
import { Pencil, Check, X } from "lucide-react";

interface EditableTextProps {
  /** Unique key for this content piece */
  contentKey: string;
  /** Default/fallback text (hardcoded original) */
  defaultText: string;
  /** HTML tag to render */
  as?: "p" | "span" | "h1" | "h2" | "h3" | "h4" | "li" | "div";
  /** Pass-through className */
  className?: string;
  /** For multi-line editing */
  multiline?: boolean;
}

const EditableText = ({
  contentKey,
  defaultText,
  as: Tag = "span",
  className = "",
  multiline = false,
}: EditableTextProps) => {
  const { editMode, getContent, saveContent } = useAdminEdit();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);

  const savedText = getContent(contentKey);
  const displayText = savedText ?? defaultText;

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = () => {
    setDraft(displayText);
    setEditing(true);
  };

  const handleSave = async () => {
    if (draft.trim() === defaultText.trim()) {
      // If reset to default, we could remove the override, but just save it
    }
    await saveContent(contentKey, draft.trim());
    setEditing(false);
  };

  const handleCancel = () => {
    setEditing(false);
    setDraft("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      handleCancel();
    }
  };

  if (!editMode) {
    return <Tag className={className}>{displayText}</Tag>;
  }

  if (editing) {
    return (
      <span className="inline-flex items-start gap-1 w-full">
        {multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 min-h-[60px] rounded-md border-2 border-primary bg-background px-2 py-1 text-sm ring-0 focus:outline-none focus:border-primary resize-y"
            dir="rtl"
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 rounded-md border-2 border-primary bg-background px-2 py-1 text-sm ring-0 focus:outline-none focus:border-primary"
            dir="rtl"
          />
        )}
        <button
          onClick={handleSave}
          className="shrink-0 w-7 h-7 rounded-md bg-primary text-primary-foreground flex items-center justify-center hover:opacity-80 transition-opacity"
          title="שמור"
        >
          <Check size={14} />
        </button>
        <button
          onClick={handleCancel}
          className="shrink-0 w-7 h-7 rounded-md bg-muted text-muted-foreground flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition-colors"
          title="ביטול"
        >
          <X size={14} />
        </button>
      </span>
    );
  }

  return (
    <Tag
      className={`${className} relative cursor-pointer ring-2 ring-primary/50 ring-offset-1 rounded-md bg-primary/5 transition-all hover:ring-primary hover:bg-primary/10`}
      onClick={startEdit}
      title="לחץ לעריכה"
    >
      {displayText}
      <span className="absolute -top-2.5 -right-2.5 bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center shadow-lg z-10 pointer-events-none animate-pulse">
        <Pencil size={10} />
      </span>
    </Tag>
  );
};

export default EditableText;
